/**
 * Lesson step engine. Every lesson is played as four chapters — Слова → Текст → Повтор →
 * Задания — each short enough for one 10–15 minute sitting. The book's `appSteps` plan
 * names the material (exercises, grammar, distinctions, tasks); this module sorts it into
 * chapters around the text:
 *
 *   Слова    key words of the text, then a few mixed recognition drills;
 *   Текст    the whole text without help, then part by part, each part followed by a
 *            question on its meaning; grammar is read on the sentences that carry it;
 *   Повтор   the lesson words back inside sentences of the text: fill the word, hear a
 *            sentence and find it, build a sentence from tiles;
 *   Задания  the book's exercises (capped), then open practical tasks — retell, answer
 *            in your own words, a real-life situation — checked by the AI tutor.
 *
 * Deliberately left out: translating whole sentences from Russian, collocation matching,
 * conjugation/form tables (e.g. country → nationality) and long runs of one drill type.
 */
import type { Exercise, Item, Lesson, PlanStep, Sentence, Word } from './content.ts';
import { allSentences, isChinese } from './content.ts';
import {
  fillFromSentence,
  fromItem,
  listeningChoice,
  meaningChoice,
  nextSentenceChoice,
  orderFromSentence,
  wordChoice,
  type AutoExercise,
  type ChoiceExercise,
} from './exercises.ts';
import { seededShuffle } from './random.ts';

/** A free-form practical task, checked by the AI tutor (or by the learner against a model). */
export interface OpenTask {
  id: string;
  mode: 'retell' | 'answer' | 'situation';
  /** Russian heading, e.g. «Перескажите историю». */
  title: string;
  /** The question or situation; in the target language when `promptLang` is "target". */
  prompt: string;
  promptLang: 'target' | 'ru';
  /** Words the answer should use. */
  keywords: string[];
  /** Retell outline (one line per part of the story). */
  outline?: string[];
}

export type StepSpec =
  | { kind: 'learn'; wordIds: string[] }
  /** Without `sentenceIds`: the whole text, read once for the gist; with them: one part. */
  | { kind: 'read'; textId: string; sentenceIds?: string[] }
  /** A question on one part of the text; AI-written when a key is set, else `fallback`. */
  | { kind: 'question'; id: string; textId: string; sentenceIds: string[]; fallback: ChoiceExercise }
  | { kind: 'grammar'; grammarIds: string[] }
  | { kind: 'exercise'; exercise: AutoExercise }
  | { kind: 'open'; task: OpenTask }
  | { kind: 'task'; taskId: string };

export interface PlannedStep {
  /** Stable key stored in lesson_progress.steps, e.g. "c2.3". */
  key: string;
  spec: StepSpec;
  minutes: number;
}

export type ChapterId = 'words' | 'text' | 'review' | 'tasks';

export const CHAPTERS: readonly { id: ChapterId; name: string }[] = [
  { id: 'words', name: 'Слова' },
  { id: 'text', name: 'Текст' },
  { id: 'review', name: 'Повтор' },
  { id: 'tasks', name: 'Задания' },
];

export interface ChapterPlan {
  /** 1-based position among the four chapters. */
  index: number;
  id: ChapterId;
  name: string;
  /** May be empty when the lesson has nothing for this chapter. */
  steps: PlannedStep[];
}

/** Words introduced up front in «Слова»; the rest are met in the text. */
export const KEY_WORDS_MAX = 15;
/** Lessons with fewer key words get the short set of drills. */
const SMALL_LESSON_WORDS = 8;
const WORD_DRILLS = { small: 3, large: 4 } as const;
const REVIEW = {
  small: { fill: 2, listen: 2, order: 1, word: 1 },
  large: { fill: 4, listen: 3, order: 2, word: 2 },
} as const;
/** Book exercises keep at most this many items each; distinctions/expansion in total. */
const BOOK_ITEMS_MAX = 4;
/** Longest run of one exercise allowed in a chapter. */
const MAX_RUN = 3;
const COMPREHENSION_MAX = 3;
const DISCOVERY_PER_GRAMMAR = 2;
const DISTINCTION_ITEMS_MAX = 3;
const EXPANSION_ITEMS_MAX = 2;
/** A text up to this many sentences is read in one part; longer ones in up to 3 parts. */
const ONE_PART_MAX = 12;
const PART_TARGET = 9;
const PARTS_MAX = 3;

/** Book exercise types that are form drills rather than practice (left out). */
const SKIPPED_BOOK_TYPES: ReadonlySet<string> = new Set(['fill_table', 'match', 'translate_ru_to_target', 'dictation']);

function exerciseInstruction(ex: Exercise): string {
  return ex.instruction_ru ?? ex.instruction ?? ex.instruction_zh ?? 'Выполните задание';
}

function exercisesFromBook(ex: Exercise, max = BOOK_ITEMS_MAX): AutoExercise[] {
  if (!ex.autoCheck || SKIPPED_BOOK_TYPES.has(ex.type)) return [];
  const instruction = exerciseInstruction(ex);
  return (ex.items ?? [])
    .map((item) => fromItem(item, item.type ?? ex.type, instruction, ex.bank))
    .filter((e): e is AutoExercise => e !== null)
    .slice(0, max);
}

/**
 * The words taught first: new words in the order the text uses them, then the remaining
 * new words in book order, at most KEY_WORDS_MAX.
 */
export function keyWords(lesson: Lesson): Word[] {
  const byId = new Map(lesson.words.map((w) => [w.id, w]));
  const ordered = new Map<string, Word>();
  for (const s of allSentences(lesson)) {
    for (const id of s.newWordIds ?? []) {
      const word = byId.get(id);
      if (word?.isNew) ordered.set(id, word);
    }
  }
  for (const w of lesson.words) if (w.isNew) ordered.set(w.id, w);
  return [...ordered.values()].slice(0, KEY_WORDS_MAX);
}

/**
 * Splits a text into reading parts along paragraph boundaries: one part for a short text,
 * else parts of about PART_TARGET sentences (at most PARTS_MAX).
 */
export function textParts(sentences: readonly Sentence[]): Sentence[][] {
  if (sentences.length <= ONE_PART_MAX) return sentences.length > 0 ? [[...sentences]] : [];
  const count = Math.min(PARTS_MAX, Math.ceil(sentences.length / PART_TARGET));
  const target = sentences.length / count;
  const parts: Sentence[][] = [[]];
  sentences.forEach((s, i) => {
    const current = parts[parts.length - 1]!;
    const paragraphChange = i > 0 && s.paragraph !== sentences[i - 1]!.paragraph;
    const full = current.length >= target * 0.75;
    if (current.length > 0 && parts.length < count && (paragraphChange ? full : current.length >= target * 1.4)) {
      parts.push([s]);
    } else {
      current.push(s);
    }
  });
  return parts;
}

/** What the book's plan refers to, in plan order. */
interface PlanRefs {
  exercises: string[];
  grammar: string[];
  discovery: boolean;
  distinctions: string[] | 'all';
  expansion: string[];
  tasks: string[];
}

function planSteps(lesson: Lesson): PlanStep[] {
  const plan = lesson.appSteps;
  if (plan.days && plan.days.length > 0) return plan.days.flatMap((d) => d.steps);
  return plan.steps ?? [];
}

function planRefs(lesson: Lesson): PlanRefs {
  const refs: PlanRefs = { exercises: [], grammar: [], discovery: false, distinctions: [], expansion: [], tasks: [] };
  const add = (list: string[], ids: string[]) => ids.forEach((id) => list.includes(id) || list.push(id));
  for (const [stepId] of planSteps(lesson)) {
    for (const part of stepId.split('+')) {
      const [name = '', arg = ''] = part.split(':');
      const ids = arg.split(/[,|]/).filter(Boolean);
      if (name === 'exercise' || name === 'exercises') add(refs.exercises, ids);
      else if (lesson.exercises.some((e) => e.id === name)) add(refs.exercises, [name]);
      else if (name === 'grammar') add(refs.grammar, ids.length > 0 ? ids : lesson.grammar.map((g) => g.id));
      else if (name === 'grammar_discovery') {
        add(refs.grammar, lesson.grammar.map((g) => g.id));
        refs.discovery = true;
      } else if (name === 'distinction') {
        if (ids.length === 0) refs.distinctions = 'all';
        else if (refs.distinctions !== 'all') add(refs.distinctions, ids);
      } else if (name === 'expansion') add(refs.expansion, ids);
      else if (name === 'task') add(refs.tasks, ids.slice(0, 1));
    }
  }
  return refs;
}

/** Grammar points with an example taken from the text are read in place, on that sentence. */
function grammarInText(lesson: Lesson, id: string): boolean {
  const sentenceIds = new Set(allSentences(lesson).map((s) => s.id));
  const point = lesson.grammar.find((g) => g.id === id);
  if (!point) return false;
  const tagged = allSentences(lesson).some((s) => s.tokens.some((t) => t.grammarId === id));
  return tagged || point.examples.some((e) => e.fromTextSentenceId && sentenceIds.has(e.fromTextSentenceId));
}

/** Round-robin over several queues, so no drill type runs long. */
function interleave<T>(queues: T[][]): T[] {
  const out: T[] = [];
  for (let i = 0; queues.some((q) => i < q.length); i += 1) {
    for (const q of queues) if (i < q.length) out.push(q[i]!);
  }
  return out;
}

const present = <T,>(list: (T | null | undefined)[]): T[] => list.filter((x): x is T => x != null);

function situationPrompt(lesson: Lesson, words: readonly string[]): string {
  const zh = isChinese(lesson);
  const list = words.join(zh ? '、' : ', ');
  const variants = zh
    ? [
        `Вы рассказываете другу похожую историю из своей жизни. Скажите 2–3 фразы по-китайски со словами: ${list}.`,
        `Вы на работе обсуждаете с коллегой-китайцем этот рассказ. Скажите 2–3 фразы со словами: ${list}.`,
        `Напишите короткое сообщение в WeChat другу о том, что вас тронуло в истории. Используйте: ${list}.`,
      ]
    : [
        `You meet a new colleague at work. Say 2–3 sentences about yourself using: ${list}.`,
        `Write a short message to a friend abroad about yourself. Use: ${list}.`,
        `You are at a conference and someone asks where you are from. Answer in 2–3 sentences using: ${list}.`,
      ];
  return seededShuffle(variants, `${lesson.id}:situation`)[0]!;
}

/** Retell (when the book has one), an own-words answer, and a life situation. */
function openTasks(lesson: Lesson, words: readonly Word[]): OpenTask[] {
  const zh = isChinese(lesson);
  const target = words.slice(0, 4).map((w) => w.word);
  const tasks: OpenTask[] = [];
  const retell = lesson.exercises.find((e) => e.type === 'retell_with_keywords');
  if (retell?.items && retell.items.length > 0) {
    const keywords = [...new Set(retell.items.flatMap((i: Item) => i.keywords ?? []))].slice(0, 8);
    tasks.push({
      id: `open-retell-${retell.id}`,
      mode: 'retell',
      title: zh ? 'Перескажите историю по-китайски' : 'Retell the story',
      prompt: retell.instruction_ru ?? 'Перескажите текст своими словами по опорным словам',
      promptLang: 'ru',
      keywords,
      outline: retell.items.map((i) => i.prompt),
    });
  } else {
    const question = lesson.tasks.flatMap((t) => t.questions ?? [])[0];
    tasks.push({
      id: 'open-answer',
      mode: 'answer',
      title: 'Ответьте своими словами',
      prompt:
        question ??
        (zh
          ? 'Что вы думаете о героях текста? Ответьте 2–3 предложениями по-китайски.'
          : 'Answer in 2–3 sentences: what did you learn about the people in the text?'),
      promptLang: question ? 'target' : 'ru',
      keywords: target.slice(0, 3),
    });
  }
  tasks.push({
    id: 'open-situation',
    mode: 'situation',
    title: 'Ситуация из жизни',
    prompt: situationPrompt(lesson, target.slice(0, 3)),
    promptLang: 'ru',
    keywords: target.slice(0, 3),
  });
  return tasks;
}

type Draft = Record<ChapterId, StepSpec[]>;

const asExercise = (exercise: AutoExercise): StepSpec => ({ kind: 'exercise', exercise });

export function buildPlan(lesson: Lesson): ChapterPlan[] {
  const draft: Draft = { words: [], text: [], review: [], tasks: [] };
  const zh = isChinese(lesson);
  const sentences = allSentences(lesson);
  const words = keyWords(lesson);
  const size = words.length < SMALL_LESSON_WORDS ? 'small' : 'large';
  const refs = planRefs(lesson);

  // 1 · Слова
  if (words.length > 0) draft.words.push({ kind: 'learn', wordIds: words.map((w) => w.id) });
  const meanings = present(words.map((w) => meaningChoice(w, lesson.words, 'Что значит это слово?')));
  const forms = present(
    [...words].reverse().map((w) => wordChoice(w, lesson.words, zh ? 'Какое слово подходит?' : 'Which word fits?')),
  );
  draft.words.push(...interleave([meanings.slice(0, 3), forms.slice(0, 2)]).slice(0, WORD_DRILLS[size]).map(asExercise));

  // 2 · Текст
  for (const text of lesson.texts) {
    draft.text.push({ kind: 'read', textId: text.id });
    for (const part of textParts(text.sentences)) {
      const ids = part.map((s) => s.id);
      draft.text.push({ kind: 'read', textId: text.id, sentenceIds: ids });
      const lastIndex = text.sentences.indexOf(part[part.length - 1]!);
      // Ask about what follows the part's middle sentence (the last one has no successor).
      const pivot = Math.max(text.sentences.indexOf(part[0]!), lastIndex - 1 - Math.floor(part.length / 2));
      const fallback =
        nextSentenceChoice(text.sentences, pivot, zh ? 'Что было дальше?' : 'What happens next?') ??
        listeningChoice(part[0]!, text.sentences, 'Послушайте и выберите, что прозвучало');
      if (fallback) draft.text.push({ kind: 'question', id: `q-${ids[0]}`, textId: text.id, sentenceIds: ids, fallback });
    }
  }
  const comprehension = refs.exercises
    .map((id) => lesson.exercises.find((e) => e.id === id))
    .filter((e): e is Exercise => e?.type === 'comprehension');
  draft.text.push(...comprehension.flatMap((e) => exercisesFromBook(e, COMPREHENSION_MAX)).map(asExercise));
  const grammarApart = refs.grammar.filter((id) => !grammarInText(lesson, id));
  if (grammarApart.length > 0) draft.text.push({ kind: 'grammar', grammarIds: grammarApart });
  if (refs.discovery) {
    const discovery = lesson.grammar.flatMap((g) =>
      present(
        (g.discovery ?? []).map((item) => fromItem(item, item.type ?? 'multiple_choice', 'Выберите правильный вариант')),
      ).slice(0, DISCOVERY_PER_GRAMMAR),
    );
    draft.text.push(...discovery.map(asExercise));
  }

  // 3 · Повтор
  const caps = REVIEW[size];
  const fills = present(words.map((w) => fillFromSentence(w, sentences, lesson.words, zh, 'Вставьте слово из текста')));
  const withWords = [...sentences].sort((a, b) => (b.newWordIds?.length ?? 0) - (a.newWordIds?.length ?? 0));
  const listens = present(
    withWords.map((s) => listeningChoice(s, sentences, 'Послушайте и выберите, что прозвучало')),
  );
  const orders = present(withWords.map((s) => orderFromSentence(s, zh ? 'Соберите предложение из текста' : 'Build the sentence')));
  const picks = present(words.map((w) => wordChoice(w, lesson.words, zh ? 'Выберите слово по значению' : 'Choose the word')))
    .slice(-caps.word);
  draft.review.push(
    ...interleave<AutoExercise>([
      fills.slice(0, caps.fill),
      listens.slice(0, caps.listen),
      orders.slice(0, caps.order),
      picks,
    ]).map(asExercise),
  );

  // 4 · Задания — book exercises, distinctions and expansion interleaved by group.
  const practice = refs.exercises
    .map((id) => lesson.exercises.find((e) => e.id === id))
    .filter((e): e is Exercise => e !== undefined && e.type !== 'comprehension')
    .map((e) => exercisesFromBook(e))
    .filter((group) => group.length > 0);
  // A lone exercise cannot be interleaved with others, so keep its run short.
  if (practice.length === 1) practice[0] = practice[0]!.slice(0, MAX_RUN);
  const distinctions = (lesson.distinctions ?? [])
    .filter((d) => refs.distinctions === 'all' || refs.distinctions.includes(d.id))
    .flatMap((d) =>
      present(
        d.practice.map((item) =>
          fromItem(item, item.type ?? 'choose_both_allowed', `${d.pair[0]} или ${d.pair[1]}? Отметьте все подходящие`),
        ),
      ),
    )
    .slice(0, DISTINCTION_ITEMS_MAX);
  const expansion = (lesson.expansion ?? [])
    .filter((x) => refs.expansion.includes(x.id) && x.practice)
    .flatMap((x) =>
      exercisesFromBook({
        ...x.practice!,
        id: x.id,
        bank: x.practice!.bank ?? x.words.map((w) => w.word),
        instruction_ru: `${x.topic_ru}: вставьте слово`,
      }),
    )
    .slice(0, EXPANSION_ITEMS_MAX);
  draft.tasks.push(...interleave([...practice, distinctions, expansion]).map(asExercise));
  draft.tasks.push(...openTasks(lesson, words).map((task): StepSpec => ({ kind: 'open', task })));
  const bookTask = refs.tasks.find((id) => lesson.tasks.some((t) => t.id === id));
  if (bookTask) draft.tasks.push({ kind: 'task', taskId: bookTask });

  return CHAPTERS.map((c, i) => ({
    index: i + 1,
    id: c.id,
    name: c.name,
    steps: draft[c.id].map((spec, n) => ({ key: `c${i + 1}.${n + 1}`, spec, minutes: stepMinutes(spec) })),
  }));
}

/** Rough time per step, for «≈ N мин» on the path. */
function stepMinutes(spec: StepSpec): number {
  switch (spec.kind) {
    case 'learn':
      return spec.wordIds.length * 0.3;
    case 'read':
      return spec.sentenceIds ? spec.sentenceIds.length * 0.4 : 3;
    case 'grammar':
      return 3;
    case 'open':
      return 4;
    case 'task':
      return 6;
    default:
      return 0.5;
  }
}

/** True when the plan has at least one playable step. */
export function hasSteps(plan: readonly ChapterPlan[]): boolean {
  return plan.some((c) => c.steps.length > 0);
}

// ---- progress ----------------------------------------------------------------------------

/**
 * Shape of lesson_progress.steps (jsonb). Step keys are positional, so a plan change bumps
 * the version and older states are discarded on read (v1: day plans, v2: first chapter plan).
 */
export interface StepsState {
  v: 3;
  done: string[];
  /** Exercise step key → answered correctly on the first attempt. */
  results: Record<string, boolean>;
  /** Finished chapters, so the path can draw them without loading the lesson. */
  chapters: ChapterId[];
}

export function emptyStepsState(): StepsState {
  return { v: 3, done: [], results: {}, chapters: [] };
}

const CHAPTER_IDS: ReadonlySet<string> = new Set(CHAPTERS.map((c) => c.id));

export function parseStepsState(raw: unknown): StepsState {
  if (typeof raw !== 'object' || raw === null) return emptyStepsState();
  const r = raw as { v?: unknown; done?: unknown; results?: unknown; chapters?: unknown };
  if (r.v !== 3) return emptyStepsState();
  return {
    v: 3,
    done: Array.isArray(r.done) ? r.done.filter((k): k is string => typeof k === 'string') : [],
    results:
      typeof r.results === 'object' && r.results !== null
        ? Object.fromEntries(
            Object.entries(r.results).filter((e): e is [string, boolean] => typeof e[1] === 'boolean'),
          )
        : {},
    chapters: Array.isArray(r.chapters)
      ? r.chapters.filter((c): c is ChapterId => typeof c === 'string' && CHAPTER_IDS.has(c))
      : [],
  };
}

export interface Position {
  chapterIndex: number;
  stepIndex: number;
}

export function isChapterComplete(chapter: ChapterPlan, state: StepsState): boolean {
  const done = new Set(state.done);
  return chapter.steps.every((s) => done.has(s.key));
}

/** First unfinished step, or null when the whole lesson is done. */
export function nextPosition(plan: readonly ChapterPlan[], state: StepsState): Position | null {
  const done = new Set(state.done);
  for (let c = 0; c < plan.length; c += 1) {
    const s = plan[c]!.steps.findIndex((step) => !done.has(step.key));
    if (s !== -1) return { chapterIndex: c, stepIndex: s };
  }
  return null;
}

/** Marks a step done and refreshes the list of finished chapters. */
export function completeStep(
  plan: readonly ChapterPlan[],
  state: StepsState,
  key: string,
  correct?: boolean,
): StepsState {
  const done = state.done.includes(key) ? state.done : [...state.done, key];
  const results =
    correct === undefined || key in state.results ? state.results : { ...state.results, [key]: correct };
  const next: StepsState = { v: 3, done, results, chapters: state.chapters };
  return { ...next, chapters: plan.filter((c) => isChapterComplete(c, next)).map((c) => c.id) };
}

/** Every step done: how a finished lesson whose stored steps predate the current plan is shown. */
export function completedState(plan: readonly ChapterPlan[]): StepsState {
  return { v: 3, done: plan.flatMap((c) => c.steps.map((s) => s.key)), results: {}, chapters: plan.map((c) => c.id) };
}

/** Starts the lesson over while keeping first-attempt results, so a replay pays no XP twice. */
export function restartLesson(state: StepsState): StepsState {
  return { ...state, done: [], chapters: [] };
}

export function isLessonComplete(plan: readonly ChapterPlan[], state: StepsState): boolean {
  return hasSteps(plan) && plan.every((c) => isChapterComplete(c, state));
}

/** Share of exercises answered correctly on the first attempt (1 when there were none). */
export function accuracy(state: StepsState, keys?: readonly string[]): number {
  const entries = Object.entries(state.results).filter(([k]) => !keys || keys.includes(k));
  if (entries.length === 0) return 1;
  return entries.filter(([, ok]) => ok).length / entries.length;
}

export function remainingMinutes(chapter: ChapterPlan, state: StepsState): number {
  const done = new Set(state.done);
  return Math.round(chapter.steps.filter((s) => !done.has(s.key)).reduce((m, s) => m + s.minutes, 0));
}

export type ChapterStatus = 'done' | 'current' | 'open' | 'locked';

/**
 * Chapter statuses for the path, from stored progress alone. `lessonStatus` is the
 * lesson's path status: a done lesson shows four done chapters, a locked one four locked.
 * In the current lesson the first unfinished chapter is `current`; in a lesson opened by
 * the placement test (below the recommended start) it is `open`.
 */
export function chapterStatuses(
  lessonStatus: 'done' | 'current' | 'open' | 'locked',
  state: StepsState | undefined,
): ChapterStatus[] {
  if (lessonStatus === 'done' || lessonStatus === 'locked') return CHAPTERS.map(() => lessonStatus);
  const finished = new Set(state?.chapters ?? []);
  let activeAssigned = false;
  return CHAPTERS.map((c) => {
    if (finished.has(c.id)) return 'done';
    if (!activeAssigned) {
      activeAssigned = true;
      return lessonStatus;
    }
    return 'locked';
  });
}
